import { ArrowRightOutlined, PlusOutlined, SwapOutlined, SyncOutlined } from '@ant-design/icons';
import { Button, Col, Form, Row } from 'antd';
import dayjs from 'dayjs';
import React, { useContext, useEffect, useState } from 'react';

import ButtonComponent from 'components/atoms/ButtonComponent';
import InputComponent from 'components/atoms/InputComponent';
import Paragraph from 'components/atoms/Paragraph';
import SelectComponent from 'components/atoms/SelectComponent';
import Title from 'components/atoms/Title';
import LoadingWrapper from 'components/hoc/LoadingWrapper';
import { Context } from 'components/hoc/Store';
import TranslateText from 'components/hoc/TranslateText';
import FormWrapper from 'components/molecules/FormWrapper';

import useNavigateWorkaround from 'hooks/useNavigateWorkaround';
import useProfily from 'hooks/useProfily';

import cisStatyApi from 'services/cisStaty';
import { EstavLeiLoginless, ILeiLoginLessData, leiCheckLoginless } from 'services/lei/leiLoginLess';

import { ETypZadosti } from 'enums/lei/leiTypZadosti';
import routePathEnum from 'enums/routePathEnum';

import { leiCountrySelect } from 'data/codebooks/lei/leiCountrySelect';
import { leiRejstrikyPermanent, leiRejstrikySelect } from 'data/codebooks/lei/leiRejstrikySelect';

import { _getKeyValue_ } from 'utils/getObjectKeyValue';
import leiRejstrikValidation from 'utils/validations/leiRejstrikValidation';
import leiValidation from 'utils/validations/leiValidation';

const BtnContentTitle = {
    fontSize: 13,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 0,
    whiteSpace: 'nowrap' as any
};
const BtnStyles = {
    padding: '8px 12px',
    position: 'relative' as any,
    height: 44,
    fontSize: 13,
    marginRight: 0,
    borderRadius: 4
};

// application type buttons - this order is the default for the `types` prop
const TYPE_BUTTONS: { typ: ETypZadosti; id: string; icon: React.ReactNode; labelKey: string }[] = [
    {
        typ: ETypZadosti.REGISTRACE,
        id: 'leiRegistrace',
        icon: <PlusOutlined />,
        labelKey: 'LEI_REGISTRACE_BTN'
    },
    {
        typ: ETypZadosti.AKTUALIZACE,
        id: 'leiAktualizace',
        icon: <SyncOutlined />,
        labelKey: 'LEI_REGISTRACE_AKTUALIZACE'
    },
    {
        typ: ETypZadosti.PREVOD,
        id: 'leiPrevod',
        icon: <SwapOutlined />,
        labelKey: 'LEI_REGISTRACE_PREVOD'
    }
];

interface ILeiZadostForm {
    /** initial selected application type (e.g. from a route param) */
    typ?: ETypZadosti;
    /** which type buttons to render - default: all of REGISTRACE, AKTUALIZACE, PREVOD */
    types?: ETypZadosti[];
    /** when set, the form is prefilled with this LEI (checked against the app API on mount) */
    initialLei?: string;
}

/**
 * LEI application form - registrace / aktualizace / prevod.
 * Extracted from the IdentifikaceSpolecnosti page so it can be reused elsewhere
 * (e.g. in the LEI search when there are no results). `types` controls which of the
 * application type buttons are shown; the first one shown is the default selection.
 */
const LeiZadostForm: React.FC<ILeiZadostForm> = ({ typ, types, initialLei }) => {
    const { navigateWorkaround } = useNavigateWorkaround();
    const { setLevel } = useProfily();
    const { lei } = useContext(Context);
    const [form] = Form.useForm();

    const visibleTypes = types ?? TYPE_BUTTONS.map(cfg => cfg.typ);
    const [selectedType, setSelectedType] = useState<ETypZadosti>(typ ?? visibleTypes[0] ?? ETypZadosti.REGISTRACE);
    const [leiError, setLeiError] = useState<string>();
    const [jineLou, setJineLou] = useState<string>();
    const [leiIcoErr, setLeiIcoErr] = useState<boolean>(false);
    const [loading, setLoading] = useState<boolean>(false);
    const [leiData, setLeiData] = useState<ILeiLoginLessData>();
    const [, rerender] = useState<boolean>(false);

    useEffect(() => {
        typ && setSelectedType(typ);

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [typ]);

    useEffect(() => {
        cisStatyApi().then(({ data }) => {
            setLevel('cisStaty', data);
        });

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // prefill from the GLEIF search ("Obnova LEI" / "Převod LEI" on a search row):
    // fill the LEI field and check it against the app API, then behave like a normal submit
    useEffect(() => {
        if (!initialLei) return;
        setLoading(true);
        form.setFieldsValue({
            lei: initialLei
        });
        leiCheckLoginless({
            typzadosti: selectedType,
            lei: initialLei
        })
            .then(resp => {
                if (resp.stav === 'OK') {
                    setJineLou('false');
                    setLeiError(undefined);
                    form.setFieldsValue({
                        lei: resp.legalentityidentifier,
                        jurisdikce: resp.jurisdikce,
                        regentity: resp.registrationauthorityentityid,
                        regauthority: resp.regauthority
                    });
                    setLeiData(resp);
                    setLevel('lei', {
                        loginless: {
                            typzadosti: selectedType,
                            spolecnost: resp
                        }
                    });
                } else if (resp.stav === EstavLeiLoginless.JINELOU) {
                    setJineLou(resp.louname);
                    setSelectedType(ETypZadosti.PREVOD);
                    setLeiError(undefined);
                } else {
                    setJineLou('false');
                    setLeiError(resp.stav);
                }
            })
            .finally(() => setLoading(false));

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialLei]);

    const selectType = (next: ETypZadosti) => {
        if (next !== ETypZadosti.PREVOD) {
            window.scrollTo({
                top: 380,
                behavior: 'smooth'
            });
        }
        setSelectedType(next);
        if (next === ETypZadosti.REGISTRACE) {
            form.setFieldsValue({
                lei: undefined
            });
        }
        setLeiIcoErr(false);
        setLeiError(undefined);
    };

    const renderTypeButtons = () => (
        <Row
            justify="space-between"
            className="lei-application-tabs"
            style={{
                justifySelf: 'stretch',
                marginTop: 6,
                marginLeft: 0,
                marginBottom: 18,
                width: '100%',
                display: 'flex',
                flexWrap: 'nowrap'
            }}
        >
            {TYPE_BUTTONS.filter(cfg => visibleTypes.includes(cfg.typ)).map(cfg => (
                <Button
                    key={cfg.typ}
                    type={selectedType === cfg.typ ? 'primary' : 'default'}
                    onClick={() => selectType(cfg.typ)}
                    // several buttons share the row equally, a single one (e.g. in the search
                    // for "Obnova"/"Převod LEI") stays at its natural width
                    style={{
                        ...BtnStyles,
                        flex: visibleTypes.length > 1 ? 1 : 'none'
                    }}
                    id={cfg.id}
                    disabled={!!leiData && selectedType !== cfg.typ}
                >
                    <Row style={BtnContentTitle}>
                        {cfg.icon}
                        <TranslateText>{cfg.labelKey}</TranslateText>
                    </Row>
                </Button>
            ))}
        </Row>
    );

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const onFinish = (value: any) => {
        console.log(value);
        if ((value.regentity && value.jurisdikce) || value.lei) {
            setLoading(true);
            leiCheckLoginless({
                typzadosti: selectedType!,
                ...(value.regentity && {
                    regentity: value.regentity
                }),
                ...(value.jurisdikce && {
                    jurisdikce: value.jurisdikce
                }),
                ...(value.regauthority && {
                    regauthority: value.regauthority
                }),
                ...(value.lei && {
                    lei: value.lei
                })
            })
                .then(resp => {
                    if (resp.stav === 'OK') {
                        setJineLou('false');
                        setLeiError(undefined);
                        form.setFieldsValue({
                            lei: resp.legalentityidentifier,
                            jurisdikce: resp.jurisdikce,
                            regentity: resp.registrationauthorityentityid,
                            regauthority: resp.regauthority
                        });
                        setLeiData(resp);
                        setLevel('lei', {
                            loginless: {
                                typzadosti: selectedType!,
                                spolecnost: resp
                            }
                        });
                    } else if (resp.stav === EstavLeiLoginless.JINELOU) {
                        setJineLou(resp.louname);
                        setSelectedType(ETypZadosti.PREVOD);
                        setLeiError(undefined);
                    } else {
                        setJineLou('false');
                        setLeiError(resp.stav);
                    }
                })
                .finally(() => setLoading(false));
        } else {
            setLeiIcoErr(true);
        }
    };

    return (
        <LoadingWrapper loading={loading}>
            <div className="lei-form-fields">
                {renderTypeButtons()}
                {selectedType && (
                    <FormWrapper outForm={form} onFinish={onFinish}>
                        {selectedType !== ETypZadosti.REGISTRACE && (
                            <>
                                <InputComponent
                                    label="LEI_CELE"
                                    name="lei"
                                    maxLength={20}
                                    type="text"
                                    rules={leiValidation()}
                                    disabled={
                                        !!leiData || form.getFieldValue('jurisdikce') || form.getFieldValue('regentity')
                                    }
                                    notRequired={true}
                                    onChange={() => rerender(pv => !pv)}
                                />
                                {!leiData && <Title level={5} dividerTitle={true} text="Nebo" />}
                            </>
                        )}
                        <SelectComponent
                            isFormItem={true}
                            name="jurisdikce"
                            labelParagraph="LEI_DETAIL_JURISDICKE"
                            data={leiCountrySelect}
                            disabled={!!leiData || form.getFieldValue('lei')}
                            notRequired={selectedType !== ETypZadosti.REGISTRACE}
                            onChange={() => {
                                form.setFieldsValue({
                                    regauthority: undefined
                                });
                                rerender(pv => !pv);
                            }}
                            onClear={() => form.resetFields(['regauthority', 'regentity'])}
                        />
                        {form.getFieldValue('jurisdikce') && (
                            <SelectComponent
                                isFormItem={true}
                                name="regauthority"
                                labelParagraph="REJSTRIK_KOD"
                                showOptionValue={true}
                                notRequired={selectedType !== ETypZadosti.REGISTRACE}
                                optionValueFilter={['RA888888']}
                                disabled={!!leiData || form.getFieldValue('lei')}
                                data={[
                                    ...(form.getFieldValue('jurisdikce')
                                        ? _getKeyValue_(form.getFieldValue('jurisdikce'))(leiRejstrikySelect)
                                        : []),
                                    ...leiRejstrikyPermanent
                                ]}
                                onChange={val => {
                                    val === 'RA000161' &&
                                        form.setFieldsValue({
                                            registrationauthorityentityid: 'ΗΕ'
                                        });
                                    rerender(pv => !pv);
                                }}
                                onClear={() => form.resetFields(['regentity'])}
                            />
                        )}
                        {form.getFieldValue('regauthority') && (
                            <InputComponent
                                label="ICO_SPOLECNOSTI"
                                name="regentity"
                                disabled={!!leiData || form.getFieldValue('lei')}
                                notRequired={selectedType !== ETypZadosti.REGISTRACE}
                                rules={leiRejstrikValidation(
                                    (form.getFieldValue('regauthority') === 'RA888888' &&
                                        form.getFieldValue('jurisdikce') === 'CZ' &&
                                        'RA000164') ||
                                        form.getFieldValue('regauthority')
                                )}
                                onChange={() => rerender(pv => !pv)}
                            />
                        )}
                        {leiData && (
                            <>
                                {leiData.legalname && (
                                    <InputComponent
                                        notRequired={true}
                                        disabled={true}
                                        label="EMP_PODANE_ZADOSTI_NAZEV_SPOLECNOSTI"
                                        name="legalname"
                                        initialValue={leiData.legalname}
                                    />
                                )}
                                {leiData.address && (
                                    <InputComponent
                                        label="LEI_DETAIL_ADRESA_TITLE"
                                        name="address"
                                        initialValue={leiData.address}
                                        notRequired={true}
                                        disabled={true}
                                    />
                                )}
                                {leiData.registrationstatus && (
                                    <InputComponent
                                        notRequired={true}
                                        disabled={true}
                                        label="STAV_LEI"
                                        name="registrationstatus"
                                        initialValue={leiData.registrationstatus}
                                    />
                                )}
                                {leiData.initialregistrationdate && (
                                    <InputComponent
                                        notRequired={true}
                                        disabled={true}
                                        label="DATUM_VYDANI"
                                        name="initialregistrationdate"
                                        initialValue={dayjs(leiData.initialregistrationdate).format('DD.MM.YYYY')}
                                    />
                                )}
                                {leiData.nextrenewaldate && (
                                    <InputComponent
                                        notRequired={true}
                                        disabled={true}
                                        label="DATUM_EXPIRACE"
                                        name="nextrenewaldate"
                                        initialValue={dayjs(leiData.nextrenewaldate).format('DD.MM.YYYY')}
                                    />
                                )}
                                {leiData.managinglou && (
                                    <InputComponent
                                        notRequired={true}
                                        disabled={true}
                                        label="SPRAVCE_LOU"
                                        name="managinglou"
                                        initialValue={leiData.managinglou}
                                    />
                                )}
                            </>
                        )}{' '}
                        {selectedType === ETypZadosti.PREVOD && (jineLou || leiData?.louname) && (
                            <Paragraph
                                text="LEI_ICO_CHECK_ERR_JINELOU_LOGINLESS"
                                className="extramessage-text"
                                style={{
                                    display: 'block',
                                    marginTop: 10,
                                    marginBottom: 20
                                }}
                                translateParams={[jineLou || leiData?.louname || '']}
                            />
                        )}
                        <Row gutter={8}>
                            {leiData && (
                                <Col>
                                    <ButtonComponent
                                        onClick={() => {
                                            setLeiData(undefined);
                                            setTimeout(() => {
                                                form.resetFields();
                                                rerender(pv => !pv);
                                            }, 0);
                                        }}
                                        buttontext="ZPET"
                                        id="osobaZpetBtn"
                                        type="default"
                                    />
                                </Col>
                            )}
                            <Col>
                                <ButtonComponent
                                    buttontext={'POKRACOVAT'}
                                    id="pokracovatBtn"
                                    htmlType="button"
                                    icon={<ArrowRightOutlined />}
                                    onClick={() => {
                                        leiData
                                            ? (setLevel('lei', {
                                                  ...lei,
                                                  loginless: {
                                                      typzadosti: selectedType!,
                                                      spolecnost: leiData
                                                  }
                                              }),
                                              navigateWorkaround(
                                                  routePathEnum.LEI_LOGINLESS_OSOBA.replace(/:hash\?$/, '')
                                              ))
                                            : form.submit();
                                    }}
                                    extramessage={
                                        leiIcoErr
                                            ? 'LEI_LOGINLESS_LEI_ICO_ERR'
                                            : leiError
                                              ? 'LEI_LOGINLESS_LEI_ERR_' + leiError
                                              : undefined
                                    }
                                />
                            </Col>
                        </Row>
                    </FormWrapper>
                )}
            </div>
        </LoadingWrapper>
    );
};

export default LeiZadostForm;
